import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Container,
  CircularProgress,
  Paper,
  Divider,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { 
  CheckCircle,
  Business,
  PersonAdd,
  Login,
  HowToReg,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { 
  useValidateInvitation,
  useAcceptInvitation
} from '../hooks/useInvitations';
import InvitationSignUp from './InvitationSignUp';

const AcceptInvitation: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, signIn } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showSignUp, setShowSignUp] = useState(false);

  // Hooks
  const { data: validation, isLoading: validating, error: validationError } = useValidateInvitation(token || null);
  const acceptInvitationMutation = useAcceptInvitation();

  const steps = [
    '招待確認',
    'ログイン',
    '招待受諾',
    '完了'
  ];

  useEffect(() => {
    if (validation) {
      if (validation.valid) {
        setActiveStep(user ? 2 : 1);
      } else {
        setError(validation.error || '無効な招待リンクです');
      }
    }
  }, [validation, user]);

  const handleAcceptInvitation = async () => {
    if (!token || !user) return;

    try {
      await acceptInvitationMutation.mutateAsync({
        token,
        userId: user.id
      });

      setActiveStep(3);
      setSuccess('招待を受諾しました。管理者としてアクセス可能になりました。');
      
      // 3秒後にダッシュボードにリダイレクト
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    } catch (err: any) {
      setError(err.message || '招待の受諾に失敗しました');
    }
  };

  const handleLoginRedirect = () => {
    // ログイン後に戻ってくるためのURLを保存
    sessionStorage.setItem('redirectAfterLogin', `/accept-invitation/${token}`);
    navigate('/login');
  };

  if (validating) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress sx={{ mb: 3 }} />
          <Typography variant="h6" color="text.secondary">
            招待を確認中...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (validationError || (validation && !validation.valid)) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Business sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            無効な招待リンク
          </Typography>
          <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
            {error || validation?.error || '招待リンクが無効です'}
          </Alert>
          <Button 
            variant="contained" 
            onClick={() => navigate('/login')}
          >
            ログインページに戻る
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* ヘッダー */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Business sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom>
            管理者招待
          </Typography>
          <Typography variant="body1" color="text.secondary">
            マンション管理組合家計簿システム
          </Typography>
        </Box>

        {/* ステッパー */}
        <Box sx={{ width: '100%', mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, width: '100%' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3, width: '100%' }}>
            {success}
          </Alert>
        )}

        <Card sx={{ width: '100%', maxWidth: 600 }}>
          <CardContent sx={{ p: 4 }}>
            {activeStep === 0 && (
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress sx={{ mb: 3 }} />
                <Typography variant="h6" gutterBottom>
                  招待を確認中...
                </Typography>
              </Box>
            )}

            {activeStep === 1 && validation?.invitation && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonAdd sx={{ mr: 1 }} />
                  管理者招待の確認
                </Typography>
                
                <Divider sx={{ mb: 3 }} />
                
                <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    招待詳細
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      組織名
                    </Typography>
                    <Typography variant="body1">
                      {validation.invitation.organization_name}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      招待メールアドレス
                    </Typography>
                    <Typography variant="body1">
                      {validation.invitation.email}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      権限
                    </Typography>
                    <Typography variant="body1">
                      管理者（{validation.invitation.role}）
                    </Typography>
                  </Box>
                </Paper>

                {!showSignUp ? (
                  <>
                    <Alert severity="info" sx={{ mb: 3 }}>
                      この招待を受諾するには、アカウントが必要です。
                    </Alert>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => navigate('/login')}
                      >
                        キャンセル
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<Login />}
                        onClick={handleLoginRedirect}
                      >
                        既存アカウントでログイン
                      </Button>
                    </Box>
                    
                    <Divider sx={{ my: 2 }}>または</Divider>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Button
                        variant="outlined"
                        startIcon={<HowToReg />}
                        onClick={() => setShowSignUp(true)}
                      >
                        新規アカウントを作成
                      </Button>
                    </Box>
                  </>
                ) : (
                  <InvitationSignUp
                    email={validation.invitation.email}
                    token={token || ''}
                    onSuccess={() => {
                      setSuccess('アカウントを作成しました。自動的に招待を受諾します...');
                      setTimeout(() => {
                        window.location.reload();
                      }, 2000);
                    }}
                  />
                )}
              </Box>
            )}

            {activeStep === 2 && validation?.invitation && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircle sx={{ mr: 1, color: 'success.main' }} />
                  招待受諾の準備完了
                </Typography>
                
                <Divider sx={{ mb: 3 }} />
                
                <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    ログイン済みユーザー
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {user?.email}
                  </Typography>
                  
                  <Typography variant="subtitle1" gutterBottom>
                    参加する組織
                  </Typography>
                  <Typography variant="body1">
                    {validation.invitation.organization_name}
                  </Typography>
                </Paper>

                <Alert severity="success" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    この招待を受諾すると、{validation.invitation.organization_name}の管理者として
                    システムにアクセスできるようになります。
                  </Typography>
                </Alert>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/dashboard')}
                  >
                    キャンセル
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={handleAcceptInvitation}
                    disabled={acceptInvitationMutation.isPending}
                  >
                    {acceptInvitationMutation.isPending ? '処理中...' : '招待を受諾する'}
                  </Button>
                </Box>
              </Box>
            )}

            {activeStep === 3 && (
              <Box sx={{ textAlign: 'center' }}>
                <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 3 }} />
                <Typography variant="h6" gutterBottom>
                  招待受諾完了！
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  管理者としてシステムにアクセスできるようになりました。
                  まもなくダッシュボードにリダイレクトします。
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => navigate('/dashboard')}
                >
                  ダッシュボードへ
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default AcceptInvitation;